import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { api } from "@/lib/api";
import { CUSTOMIZABLE_TOKENS, type CustomMap, type CustomToken } from "./themeConstants";

export type AdminAppearance = "light" | "dark";
export type AdminTheme = {
  appearance: AdminAppearance;
  preset: string;
  custom: CustomMap;
};

export const ADMIN_THEME_LOCAL_PREFIX = "nana-admin-theme:";
export const DEFAULT_ADMIN_THEME: AdminTheme = { appearance: "light", preset: "", custom: {} };

const ALLOWED_CUSTOM_KEYS = new Set<CustomToken>(CUSTOMIZABLE_TOKENS.map((t) => t.key));

export const ADMIN_THEME_PRESETS = [
  { id: "", label: "Hồng Admin", swatch: "#db2777", tokens: { primary: "#db2777", accent: "#f472b6", ring: "#db2777" } },
  { id: "cyan", label: "Cyan", swatch: "#0891b2", tokens: { primary: "#0891b2", accent: "#22d3ee", ring: "#0891b2" } },
  { id: "lime", label: "Lime", swatch: "#65a30d", tokens: { primary: "#65a30d", accent: "#a3e635", ring: "#65a30d" } },
  { id: "violet", label: "Tím", swatch: "#7c3aed", tokens: { primary: "#7c3aed", accent: "#a78bfa", ring: "#7c3aed" } },
  { id: "amber", label: "Cam", swatch: "#d97706", tokens: { primary: "#d97706", accent: "#fbbf24", ring: "#d97706" } },
  { id: "ocean", label: "Đại dương", swatch: "#2563eb", tokens: { primary: "#2563eb", accent: "#38bdf8", ring: "#2563eb" } },
  { id: "sunset", label: "Hoàng hôn", swatch: "#ea580c", tokens: { primary: "#ea580c", accent: "#fb7185", ring: "#ea580c" } },
  { id: "mint", label: "Bạc hà", swatch: "#059669", tokens: { primary: "#059669", accent: "#5eead4", ring: "#059669" } },
  { id: "rose", label: "Hồng đào", swatch: "#e11d48", tokens: { primary: "#e11d48", accent: "#fb7185", ring: "#e11d48" } },
  { id: "mono", label: "Đơn sắc", swatch: "#475569", tokens: { primary: "#475569", accent: "#94a3b8", ring: "#475569" } },
] as const;

const ADMIN_LIGHT_TOKENS: Record<string, string> = {
  background: "#f8fafc",
  foreground: "#0f172a",
  card: "#ffffff",
  "card-foreground": "#0f172a",
  popover: "#ffffff",
  "popover-foreground": "#0f172a",
  primary: "#db2777",
  "primary-foreground": "#ffffff",
  secondary: "#e2e8f0",
  "secondary-foreground": "#0f172a",
  muted: "#eef2f7",
  "muted-foreground": "#64748b",
  accent: "#fce7f3",
  "accent-foreground": "#831843",
  destructive: "#dc2626",
  "destructive-foreground": "#ffffff",
  border: "#dbe3ef",
  input: "#e2e8f0",
  ring: "#db2777",
  sidebar: "#ffffff",
  "sidebar-foreground": "#0f172a",
  "sidebar-primary": "#db2777",
  "sidebar-primary-foreground": "#ffffff",
  "sidebar-accent": "#f1f5f9",
  "sidebar-accent-foreground": "#0f172a",
  "sidebar-border": "#dbe3ef",
  "sidebar-ring": "#db2777",
};

const ADMIN_DARK_TOKENS: Record<string, string> = {
  background: "#15141d",
  foreground: "#f5f0f3",
  card: "#1d1b27",
  "card-foreground": "#f5f0f3",
  popover: "#1d1b27",
  "popover-foreground": "#f5f0f3",
  primary: "#ff3d7a",
  "primary-foreground": "#15141d",
  secondary: "#27242f",
  "secondary-foreground": "#f5f0f3",
  muted: "#27242f",
  "muted-foreground": "#a89faa",
  accent: "#ff6fbf",
  "accent-foreground": "#15141d",
  destructive: "#e8584b",
  "destructive-foreground": "#ffffff",
  border: "#332f3c",
  input: "#27242f",
  ring: "#ff3d7a",
  sidebar: "#1d1b27",
  "sidebar-foreground": "#f5f0f3",
  "sidebar-primary": "#ff3d7a",
  "sidebar-primary-foreground": "#15141d",
  "sidebar-accent": "#27242f",
  "sidebar-accent-foreground": "#f5f0f3",
  "sidebar-border": "#332f3c",
  "sidebar-ring": "#ff3d7a",
};

export function adminTokenDefault(key: CustomToken, appearance: AdminAppearance = "light"): string {
  return (appearance === "dark" ? ADMIN_DARK_TOKENS : ADMIN_LIGHT_TOKENS)[key] ?? "#000000";
}

function sanitizeTheme(input: unknown): AdminTheme {
  const obj = input && typeof input === "object" ? input as Partial<AdminTheme> : {};
  const appearance: AdminAppearance = obj.appearance === "dark" ? "dark" : "light";
  const preset = ADMIN_THEME_PRESETS.some((p) => p.id === obj.preset) ? (obj.preset ?? "") : "";
  const custom: CustomMap = {};
  if (obj.custom && typeof obj.custom === "object") {
    for (const [key, value] of Object.entries(obj.custom)) {
      if (ALLOWED_CUSTOM_KEYS.has(key as CustomToken) && typeof value === "string" && value.trim()) {
        custom[key as CustomToken] = value.trim();
      }
    }
  }
  return { appearance, preset, custom };
}

export function readAdminThemeLocal(userId?: string | null): AdminTheme {
  if (!userId || typeof window === "undefined") return DEFAULT_ADMIN_THEME;
  try {
    return sanitizeTheme(JSON.parse(localStorage.getItem(ADMIN_THEME_LOCAL_PREFIX + userId) ?? "{}"));
  } catch {
    return DEFAULT_ADMIN_THEME;
  }
}

export function writeAdminThemeLocal(userId: string | null | undefined, theme: AdminTheme) {
  if (!userId || typeof window === "undefined") return;
  try { localStorage.setItem(ADMIN_THEME_LOCAL_PREFIX + userId, JSON.stringify(sanitizeTheme(theme))); } catch {}
}

export async function fetchAdminTheme(): Promise<AdminTheme | null> {
  try {
    const payload = await api.get<AdminTheme>("/admin-theme");
    return sanitizeTheme(payload);
  } catch {
    return null;
  }
}

export async function saveAdminTheme(theme: AdminTheme): Promise<boolean> {
  try {
    await api.put("/admin-theme", sanitizeTheme(theme));
    return true;
  } catch {
    return false;
  }
}

export function buildAdminThemeStyle(theme: AdminTheme): CSSProperties & Record<string, string> {
  const clean = sanitizeTheme(theme);
  const base = clean.appearance === "dark" ? ADMIN_DARK_TOKENS : ADMIN_LIGHT_TOKENS;
  const preset = ADMIN_THEME_PRESETS.find((p) => p.id === clean.preset) ?? ADMIN_THEME_PRESETS[0];
  const tokens = { ...base, ...preset.tokens, ...clean.custom } as Record<string, string>;
  tokens["sidebar-primary"] = tokens.primary;
  tokens["sidebar-ring"] = tokens.ring;
  tokens["neon-a"] = tokens.primary;
  tokens["neon-b"] = tokens.accent;
  const style: CSSProperties & Record<string, string> = { colorScheme: clean.appearance } as CSSProperties & Record<string, string>;
  for (const [key, value] of Object.entries(tokens)) style[`--${key}`] = value;
  style["--gradient-neon"] = `linear-gradient(135deg, ${tokens.primary}, ${tokens.accent})`;
  style["--gradient-surface"] = `linear-gradient(180deg, ${tokens.card}, ${tokens.background})`;
  style["--glow-pink"] = `0 0 24px color-mix(in oklab, ${tokens.primary} 28%, transparent)`;
  style["--glow-pink-soft"] = `0 0 14px color-mix(in oklab, ${tokens.accent} 22%, transparent)`;
  return style;
}

export function useAdminTheme(userId?: string | null) {
  const [theme, setThemeState] = useState<AdminTheme>(DEFAULT_ADMIN_THEME);
  const themeRef = useRef<AdminTheme>(DEFAULT_ADMIN_THEME);

  const setTheme = useCallback((next: AdminTheme) => {
    const clean = sanitizeTheme(next);
    themeRef.current = clean;
    setThemeState(clean);
    writeAdminThemeLocal(userId, clean);
    if (userId) void saveAdminTheme(clean);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      themeRef.current = DEFAULT_ADMIN_THEME;
      setThemeState(DEFAULT_ADMIN_THEME);
      return;
    }
    const local = readAdminThemeLocal(userId);
    themeRef.current = local;
    setThemeState(local);
    let alive = true;
    void fetchAdminTheme().then((remote) => {
      if (!alive || !remote) return;
      themeRef.current = remote;
      setThemeState(remote);
      writeAdminThemeLocal(userId, remote);
    });
    return () => { alive = false; };
  }, [userId]);

  return { theme, setTheme };
}